import { createI18n } from 'vue-i18n';
import enUS from './en-US';
import zhCN from './zh-CN';

/**
 * A11 i18n 架构（★ 语言清单来自平台 `locales` 表，不是这里写死的数组）：
 *   - `messages` 只决定"这个 UI 打包了哪几种界面语言"；
 *   - 可选语言 = 表里 enabled 的 ∩ 这里装了包的；
 *   - 平台新装一种语言但 UI 包还没写 ⇒ 该语言不进下拉（而不是界面显示 raw key）。
 */
export const messages = { zh: zhCN, en: enUS } as const;
export type UiLocale = keyof typeof messages;

/** 兜底语言：包一定存在（否则任何缺键都会变成裸 key） */
export const FALLBACK_LOCALE: UiLocale = 'en';
export const DEFAULT_LOCALE: UiLocale = 'zh';

export const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LOCALE,
  fallbackLocale: FALLBACK_LOCALE,
  messages,
});

/** 表里装的 locale ∩ 前端装了 UI 包的 locale（顺序沿用接口返回的 sort_order） */
export function supportedUiLocales(available: string[]): UiLocale[] {
  const packed = Object.keys(messages) as UiLocale[];
  const out: UiLocale[] = [];
  for (const code of available) {
    if ((packed as string[]).includes(code) && !out.includes(code as UiLocale)) out.push(code as UiLocale);
  }
  return out.length > 0 ? out : [DEFAULT_LOCALE];
}

/**
 * 路由里的 :locale 决议（★ 未知语言不静默回落，返回 null 让路由层跳回默认语言）：
 * 浏览器偏好只在"没带 locale 前缀"时参与，带前缀就以 URL 为准（可分享链接不能被人改语言）。
 */
export function resolveLocale(
  param: string | undefined,
  available: UiLocale[],
  navigatorLanguage?: string,
): UiLocale | null {
  if (param) return (available as string[]).includes(param) ? (param as UiLocale) : null;
  const guess = (navigatorLanguage ?? '').slice(0, 2).toLowerCase();
  return (available as string[]).includes(guess)
    ? (guess as UiLocale)
    : available.includes(DEFAULT_LOCALE)
      ? DEFAULT_LOCALE
      : (available[0] ?? DEFAULT_LOCALE);
}
